'use client';

import {
  createSecureInformationRequestTemplate,
  useSecureInformationRequestTemplates,
} from '@commenda/api';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FieldWrapper,
  Input,
  Label,
  useToggle,
} from '@commenda/kakashi';
import { SecureInformationRequestFieldType } from '@prisma/client';
import { useState } from 'react';
import { useNotification } from '../../hooks/useNotification';
import { GroupProps } from './types';

type SaveTemplateModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  agentFirmId?: string;

  groups: GroupProps[];
};

const SaveTemplateModal = ({
  isOpen,
  onClose,
  onConfirm,
  agentFirmId,
  groups,
}: SaveTemplateModalProps) => {
  const isSavingTemplate = useToggle();

  const [title, setTitle] = useState<string | null>(null);

  const { mutate: mutateTemplates } =
    useSecureInformationRequestTemplates(agentFirmId);

  const { setNotification } = useNotification();

  const onSaveTemplate = async () => {
    isSavingTemplate.on();

    const res = await createSecureInformationRequestTemplate(
      {
        groups: groups.map((group, idx) => {
          return {
            headerText: group.headerText,
            indexInRequest: idx + 1,
            fields: group.fields.map((field, index) => {
              return {
                headerText: field.headerText,
                type: field.type,
                isSecure: field.isSecure,
                indexInGroup: index + 1,
                ...(field.type === SecureInformationRequestFieldType.SELECT ||
                field.type === SecureInformationRequestFieldType.MULTI_SELECT
                  ? { selectOptions: field.selectOptions }
                  : {}),
                ...(field.tooltip ? { tooltipText: field.tooltip } : {}),
              };
            }),
          };
        }),
        title,
        ...(agentFirmId ? { agentFirmId } : {}),
      },
      undefined
    );

    if (!res.ok) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message:
          'Something went wrong, please try again or contact Team Commenda if you need help',
      });
      await mutateTemplates();
      isSavingTemplate.off();
      return;
    }

    isSavingTemplate.off();
    onConfirm();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent hideCloseIcon>
        <DialogHeader className="flex flex-col items-start gap-xs">
          <DialogTitle>Save this template?</DialogTitle>
          <DialogDescription>
            If you save this form as a template, it can be used by you and other
            team mates in future.
          </DialogDescription>
        </DialogHeader>

        <div className="p-2xl">
          <FieldWrapper>
            <Label>Template name</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter name"
            />
          </FieldWrapper>
        </div>

        <DialogFooter>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={isSavingTemplate.value}
          >
            Do not save template
          </Button>
          <Button
            variant="primary"
            onClick={onSaveTemplate}
            disabled={isSavingTemplate.value || !title}
          >
            Save template
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export { SaveTemplateModal };
